import { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { EntityType } from '@/types/link';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useSupplierStore } from '@/stores/supplierStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useEndpointStore } from '@/stores/endpointStore';
import { useCloudResourceStore } from '@/stores/cloudResourceStore';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';
import {
  Search, Building2, Layers, Server, Monitor, Cloud, Globe, Shield,
  ClipboardList, ShieldCheck, AlertTriangle, AlertCircle, Users, FileCheck
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

export type EntityOption = {
  id: string;
  name: string;
};

interface EntitySelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (entityType: EntityType, entities: EntityOption[]) => void;
  allowedTypes?: EntityType[];
  multiSelect?: boolean;
  title?: string;
  excludeEntityType?: EntityType;
  excludeEntityId?: string;
}

const ENTITY_TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType }> = {
  site: { label: 'Sites', icon: Building2 },
  zone: { label: 'Zones', icon: Layers },
  system: { label: 'Systems', icon: Server },
  endpoint: { label: 'Endpoints', icon: Monitor },
  cloud_resource: { label: 'Cloud Resources', icon: Cloud },
  internet_connection: { label: 'Internet', icon: Globe },
  dmz_config: { label: 'DMZ', icon: Shield },
  task: { label: 'Tasks', icon: ClipboardList },
  control: { label: 'Controls', icon: ShieldCheck },
  risk: { label: 'Risks', icon: AlertTriangle },
  incident: { label: 'Incidents', icon: AlertCircle },
  supplier: { label: 'Suppliers', icon: Users },
  evidence: { label: 'Evidence', icon: FileCheck },
};

export function EntitySelectorDialog({
  open,
  onOpenChange,
  onSelect,
  allowedTypes = ['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'task', 'control', 'risk', 'incident', 'supplier', 'evidence'],
  multiSelect = true,
  title = 'Select Entities to Link',
  excludeEntityType,
  excludeEntityId,
}: EntitySelectorDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<EntityType>(allowedTypes[0]);
  const [controls, setControls] = useState<EntityOption[]>([]);
  const [incidents, setIncidents] = useState<EntityOption[]>([]);

  const { organization } = useAuth();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const { suppliers, loadSuppliers } = useSupplierStore();
  const { sites, loadSites } = useSiteStore();
  const { zones, loadZones } = useZoneStore();
  const { systems, loadSystems } = useSystemStore();
  const { endpoints, loadEndpoints } = useEndpointStore();
  const { cloudResources, loadCloudResources } = useCloudResourceStore();
  const { connections: internetConnections, loadConnections: loadInternetConnections } = useInternetConnectionStore();
  const { configs: dmzConfigs, loadConfigs: loadDMZConfigs } = useDMZConfigStore();

  // Load all stores when dialog opens
  useEffect(() => {
    if (open) {
      loadSites();
      loadZones();
      loadSystems();
      loadEndpoints();
      loadCloudResources();
      loadInternetConnections();
      loadDMZConfigs();
      loadSuppliers();
    }
  }, [open, loadSites, loadZones, loadSystems, loadEndpoints, loadCloudResources, loadInternetConnections, loadDMZConfigs, loadSuppliers]);

  // Reset selection when dialog opens/closes or tab changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch('');
      setActiveTab(allowedTypes[0]);
    }
  }, [open, allowedTypes]);

  // Fetch controls and incidents
  useEffect(() => {
    let isActive = true;

    const loadEntities = async () => {
      if (allowedTypes.includes('control')) {
        const { data, error } = await supabase
          .from('controls')
          .select('id, title')
          .order('title');

        if (!error && isActive) {
          setControls((data ?? []).map(item => ({ id: item.id, name: item.title })));
        }
      } else if (isActive) {
        setControls([]);
      }

      if (allowedTypes.includes('incident')) {
        const { data, error } = await supabase
          .from('incidents')
          .select('id, title')
          .order('title');

        if (!error && isActive) {
          setIncidents((data ?? []).map(item => ({ id: item.id, name: item.title })));
        }
      } else if (isActive) {
        setIncidents([]);
      }
    };

    if (open) {
      void loadEntities();
    }

    return () => {
      isActive = false;
    };
  }, [allowedTypes, open]);

  const getEntitiesForType = useCallback((type: EntityType): EntityOption[] => {
    switch (type) {
      case 'site':
        return sites.map(site => ({ id: site.id, name: site.name }));
      case 'zone':
        return zones.map(zone => ({ id: zone.id, name: zone.name }));
      case 'system':
        return systems.map(system => ({ id: system.id, name: system.name }));
      case 'endpoint':
        return endpoints.map(ep => ({ id: ep.id, name: ep.name }));
      case 'cloud_resource':
        return cloudResources.map(cr => ({ id: cr.id, name: cr.name }));
      case 'internet_connection':
        return internetConnections.map(ic => ({ id: ic.id, name: ic.label }));
      case 'dmz_config':
        return dmzConfigs.map(dmz => ({ id: dmz.id, name: dmz.label }));
      case 'task':
        return (tasks ?? []).map(t => ({ id: t.id, name: t.title }));
      case 'control':
        return controls;
      case 'risk':
        return (risks ?? []).map(risk => ({ id: risk.id, name: risk.title }));
      case 'incident':
        return incidents;
      case 'supplier':
        return suppliers.map(supplier => ({ id: supplier.id, name: supplier.name }));
      case 'evidence':
        return (evidence ?? []).map(ev => ({ id: ev.id, name: ev.name }));
      default:
        return [];
    }
  }, [cloudResources, controls, dmzConfigs, endpoints, evidence, incidents, internetConnections, risks, sites, suppliers, systems, tasks, zones]);

  const filteredEntities = useMemo(() => {
    let entities = getEntitiesForType(activeTab);
    
    // Exclude the current entity to prevent self-linking
    if (excludeEntityType === activeTab && excludeEntityId) {
      entities = entities.filter(e => e.id !== excludeEntityId);
    }
    
    if (!search) return entities;
    return entities.filter(entity =>
      entity.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [activeTab, getEntitiesForType, search, excludeEntityType, excludeEntityId]);

  const toggleSelection = useCallback((id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      if (!multiSelect) {
        newSelection.clear();
      }
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  }, [multiSelect, selectedIds]);

  const handleConfirm = useCallback(() => {
    const entities = getEntitiesForType(activeTab);
    const selectedEntities = entities.filter(e => selectedIds.has(e.id));
    onSelect(activeTab, selectedEntities);
    setSelectedIds(new Set());
    setSearch('');
    onOpenChange(false);
  }, [activeTab, getEntitiesForType, onOpenChange, onSelect, selectedIds]);

  const getEntityConfig = (type: EntityType) => {
    return ENTITY_TYPE_CONFIG[type] || { label: type.replace('_', ' '), icon: Server };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-1.5 pb-2 border-b">
          {allowedTypes.map(type => {
            const config = getEntityConfig(type);
            const Icon = config.icon;
            const count = getEntitiesForType(type).length;
            const isActive = activeTab === type;
            
            return (
              <button
                key={type}
                onClick={() => {
                  setActiveTab(type);
                  setSelectedIds(new Set());
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{config.label}</span>
                <Badge variant={isActive ? "secondary" : "outline"} className="ml-1 h-5 px-1.5 text-xs">
                  {count}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${getEntityConfig(activeTab).label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Entity List */}
        <ScrollArea className="flex-1 min-h-[300px] border rounded-md">
          <div className="p-2 space-y-1">
            {filteredEntities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No {getEntityConfig(activeTab).label.toLowerCase()} found
              </p>
            ) : (
              filteredEntities.map(entity => (
                <div
                  key={entity.id}
                  className={cn(
                    "flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors",
                    selectedIds.has(entity.id) 
                      ? "bg-primary/10 border border-primary/30" 
                      : "hover:bg-muted"
                  )}
                  onClick={() => toggleSelection(entity.id)}
                >
                  <Checkbox
                    checked={selectedIds.has(entity.id)}
                    onCheckedChange={() => toggleSelection(entity.id)}
                  />
                  <span className="text-sm flex-1 truncate">{entity.name}</span>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedIds.size === 0}>
            Link {selectedIds.size} {selectedIds.size === 1 ? 'Item' : 'Items'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
